// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

export const IS_PRODUCTION = import.meta.env.MODE === 'production';
export const IS_DEVELOPMENT = import.meta.env.MODE === 'development';

// ============================================================================
// HARDCODED CONFIGURATION
// ============================================================================

/**
 * Build timestamp (ISO 8601).
 */
export const BUILD_TIME = new Date().toISOString();

// ============================================================================
// BUILD CONFIGURATION
// ============================================================================

/**
 * Controls whether console logs are removed from production builds.
 * Configured at build-time in vite.config.js
 */
export const DROP_CONSOLE_IN_PRODUCTION = import.meta.env.VITE_DROP_CONSOLE !== 'false';

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * URL-ul de bază al backendului.
 *
 * Vine din `VITE_APP_BACKEND_URL` (fișierul `.env`, gitignored). Dacă variabila
 * lipsește — cazul build-ului din CI — se folosește backendul de PRODUCȚIE.
 *
 * Pentru development local cu backend pe `:8080`, pune în `.env`:
 *     VITE_APP_BACKEND_URL=http://localhost:8080
 *
 * ⚠️ De ce nu mai e un `const` în cod: până în aug 2026 exista aici un comutator
 * `USE_LOCAL_BACKEND`, într-un fișier TRACKED de git. Lăsat pe `true` și prins
 * într-un commit, ar fi făcut ca frontendul de producție
 * (scoalaecoterrienilor.ecotic.ro) să ceară date de la `localhost:8080` — adică
 * site-ul public s-ar fi rupt complet. Acum valoarea locală stă într-un fișier
 * gitignored și nu mai poate ajunge din greșeală în producție.
 *
 * Notă: nu există proxy `/api` în `staticwebapp.config.json`, iar SKU-ul Static
 * Web App e Free (linked backends nici nu sunt posibile). Aplicația apelează
 * backendul direct, prin URL absolut — de aceea fallback-ul NU e string gol.
 */
export const BACKEND_URL: string =
    import.meta.env.VITE_APP_BACKEND_URL?.trim() || "https://backend-ecotic.azurewebsites.net";

/** `true` când rulezi împotriva unui backend local. Derivat, nu configurat. */
export const USE_LOCAL_BACKEND: boolean = /localhost|127\.0\.0\.1/.test(BACKEND_URL);

/**
 * DEPRECATED: Use BACKEND_URL directly instead
 * NOTE: Generated OpenAPI endpoints already include /api prefix (e.g., "/api/schools")
 * This constant exists for backward compatibility with auth.api.ts
 */
export const BASE_FETCH_URL = `${BACKEND_URL}/api` as const;

// ============================================================================
// AUTHENTICATION STORAGE KEYS (HARDCODED)
// ============================================================================

/**
 * LocalStorage key for JWT access token
 */
export const TOKEN_KEY = "ecotic-auth-dev-token";

/**
 * LocalStorage key for JWT refresh token
 */
export const REFRESH_TOKEN_KEY = "ecotic-auth-dev-refreshToken";

/**
 * LocalStorage key for access token expiration timestamp
 * Stores Date.now() + (expiresIn * 1000) for proactive refresh
 */
export const TOKEN_EXPIRES_AT_KEY = "ecotic-auth-dev-tokenExpiresAt";

// ============================================================================
// REDIRECT URL STORAGE KEYS (HARDCODED)
// ============================================================================

/**
 * LocalStorage key for storing pathname for post-login redirect
 */
export const REDIRECT_PATHNAME_KEY = "ecotic-dev-redirect-pathname";

/**
 * LocalStorage key for storing query string for post-login redirect
 */
export const REDIRECT_SEARCH_KEY = "ecotic-dev-redirect-search";

/**
 * LocalStorage key for storing hash for post-login redirect
 */
export const REDIRECT_HASH_KEY = "ecotic-dev-redirect-hash";

// ============================================================================
// APPLICATION IDENTIFIERS
// ============================================================================

export const ECOTIC_ID = "ec410f81-05ea-4c89-8961-7e473a30c89d" as const;

// Feature Flags Configuration
export const FEATURE_FLAG_CONFIG = {
    FORCE_SCHOOL_UPDATE: {
        enabled: false,
        cutoffDate: 1665100800, // October 7, 2022
        message: "Va rugam actualizati informatiile scolii",
        toastDuration: 120000,
        redirectPath: "/detalii-scoala-v2",
    },
    PROFESSOR_REGISTRATION: {
        enabled: true,
        disabledMessage: "Înscrierile pentru profesori sunt momentan închise",
    },
    SCHOOL_DETAILS_EDITING: {
        enabled: true,
        menuText: "Actualizare Detalii Scoala",
        menuBold: true,
    },
    LESSONS: {
        enabled: false,
    },
} as const;

/**
 * Preluarea unei scoli de catre alt profesor.
 *
 * TERMEN_ZILE e un reper ORIENTATIV: dupa atatea zile, o cerere de preluare inca nedecisa se
 * evidentiaza in raportul de admin. Nu declanseaza nimic automat - nici aprobare, nici respingere,
 * nici email. Decizie owner 2026-08-20.
 */
export const PRELUARE_CONFIG = {
    TERMEN_ZILE: 3,
} as const;

/**
 * Contact ECOTIC afisat in dropdown-ul de user (NavUser). Telefonul vine din
 * `VITE_CONTACT_ECOTIC_TELEFON`, cu fallback pe valoarea afisata azi — ca sa poata fi
 * actualizat fara un nou deploy de cod (SE-25/08, owner 2026-09-16). FARA nume de persoana:
 * era hardcodat in NavUser, scos din tracking.
 *
 * 🔴 Telefonul se scrie in **format international** (`+40…`), nu national. Valoarea de aici ajunge
 * atat in textul afisat, cat si in `href="tel:…"` (prin `hrefTelefon`), iar pe o pagina publica
 * `href`-ul cere prefixul de tara (RFC 2806). Numarul vechi, scris `0755…`, e scos: era si un numar
 * diferit de cel oficial, deci meniul si panoul de scoala ocupata aratau doua numere diferite.
 */
export const CONTACT_ECOTIC = {
    program: "L-V 09:00-15:00",
    telefon: import.meta.env.VITE_CONTACT_ECOTIC_TELEFON?.trim() || "+40752301002",
    email: "scoli@ecotic.ro",
} as const;

// ============================================================================
// DOCUMENT LINKS (CENTRALIZED)
// ============================================================================

// REGULAMENT si MECANISM sunt fisiere statice in acest bundle (decizie owner 2026-09-17: gazduire
// statica pana in weekend, apoi Cloudinary). COLABORATORI ramane pe placeholder-ul 2025-2026 —
// ECOTIC nu a livrat inca o prezentare noua pentru colaboratori (intrebare deschisa catre ECOTIC,
// vezi docs/superpowers/plans/2026-09-17-ecotic-runda2.md).
//
// Sursa reala ramane backendul (`GET /api/editions/current/documents`, `use-edition-documents.ts`):
// valorile de aici sunt doar fallback-ul afisat instant, inainte sa soseasca raspunsul.
export const DOCUMENT_LINKS = {
    /** Protocol de colaborare 2026-2027 — static in bundle, ca mecanismul si regulamentul */
    PROTOCOL_COLABORARE: "/documente/protocol-colaborare-2026-2027.pdf",
    /** Regulament 2026-2027 — static in bundle, pana la mutarea pe Cloudinary (weekend) */
    REGULAMENT: "/documente/regulament-2026-2027.pdf",
    /** Mecanism 2026-2027 — static in bundle, pana la mutarea pe Cloudinary (weekend) */
    MECANISM: "/documente/mecanism-2026-2027.pdf",
    /** ⚠️ TEMPORAR (editia a VIII-a, 2025-2026) — de inlocuit cand ECOTIC livreaza documentul nou */
    COLABORATORI: "https://res.cloudinary.com/ddrkurepu/image/upload/v1770539735/Prezentare_Colaboratori_-_Mecanism_SE_editia_a_VIII-a_-_2025-2026_seanj8.pdf",
    /** Nota de informare - inscriere elevi */
    NOTA_INFORMARE_ELEVI: "https://www.ecotic.ro/nota-de-informare-inscriere-elevi-in-scoala-ecoterrienilor-2/",
    /** Nota de informare - inscriere profesori */
    NOTA_INFORMARE_PROFESORI: "https://www.ecotic.ro/nota-de-informare-inscriere-profesori-scoala-in-scoala-ecoterrienilor/",
    /** Informare generala Scoala Ecoterrienilor */
    INFORMARE_SCOALA: "https://www.ecotic.ro/informare-scoala-ecoterrienilor/",
    /** Politica de confidentialitate ECOTIC */
    POLITICA_CONFIDENTIALITATE: "https://ecotic.ro/politica-de-confidentialitate-si-de-prelucrare-datelor-cu-caracter-personal",
    /**
     * Politica de cookie-uri ECOTIC („Informații despre cookie-uri" din subsolul ecotic.ro, 22.09).
     * Descrie site-ul lor, nu platforma: cookie-urile platformei sunt listate in bannerul propriu
     * (`core/consent/cookie-consent.ts`). O politica dedicata platformei ramane de scris de ECOTIC.
     */
    POLITICA_COOKIES: "https://www.ecotic.ro/cookie-policy/",
} as const;

/**
 * Calendarul recomandat pentru activitati de pe «Acasă» (PROFESOR, macheta owner-ului 21.09).
 *
 * `null` cat timp ECOTIC nu a dat imaginea: pagina arata un loc rezervat. Cand vine PNG-ul, se pune in
 * `public/documente/` si constanta primeste calea (ex. `"/documente/calendar-2026-2027.png"`).
 */
export const CALENDAR_RECOMANDAT_IMG: string | null = null;
